import {
  BufferGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Vector3,
} from "three";

import { EARTH_RADIUS } from "./constants";
import { latLngToVec3 } from "./geo";
import { geojsonToLineSegments, type GeoJSON } from "./lines";

// Land borders between countries — Natural Earth 1:50m, built by
// scripts/build-layer-data.mjs.
const DATA_URL = "/data/countries.geojson";
const LINE_RADIUS = EARTH_RADIUS * 1.002;

export async function loadCountryBorders(): Promise<
  LineSegments<BufferGeometry, LineBasicMaterial>
> {
  const res = await fetch(DATA_URL);
  if (!res.ok) throw new Error(`countries: ${res.status}`);
  return geojsonToLineSegments((await res.json()) as GeoJSON, LINE_RADIUS);
}

// Coastlines — the same Natural Earth 1:50m land outlines the Time
// zones map paints its land from (public/data/land.json). Drawn as
// vector lines, the coast stays sharp at any zoom, where the painted
// land (a 4096 px texture, ~10 km a pixel) would soften.
const LAND_URL = "/data/land.json";
const COAST_RADIUS = EARTH_RADIUS * 1.0015;

export async function loadCoastlines(): Promise<
  LineSegments<BufferGeometry, LineBasicMaterial>
> {
  const res = await fetch(LAND_URL);
  if (!res.ok) throw new Error(`land: ${res.status}`);
  const { polygons } = (await res.json()) as { polygons: number[][][] };
  const rings = polygons.flat();
  const positions: number[] = [];
  const a = new Vector3();
  const b = new Vector3();
  for (const ring of rings) {
    for (let i = 0; i < ring.length - 2; i += 2) {
      const [lng1, lat1, lng2, lat2] = ring.slice(i, i + 4);
      // Rings are unwrapped, so no step jumps the date line; a ring
      // circling the pole is closed through it, and that closure is
      // not coast.
      if (Math.abs(lat1) >= 90 || Math.abs(lat2) >= 90) continue;
      // Antarctica's ice is painted from the globe's land mask; its own
      // edge is the coast there, and a vector line beside it would part.
      if (lat1 < -60 && lat2 < -60) continue;
      latLngToVec3(lat1, lng1, COAST_RADIUS, a);
      latLngToVec3(lat2, lng2, COAST_RADIUS, b);
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  }
  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  const mesh = new LineSegments(
    geo,
    new LineBasicMaterial({ transparent: true, depthWrite: false }),
  );
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.visible = false;
  return mesh;
}
